import { DestroyRef, Directive, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormGroup } from '@angular/forms';
import { NumericInputHelperUtil } from '@zero/shared/util-helper';
import { Asset, ExecutionType, InstrumentType, OrderDirection, Position, Quote } from '@zero/shared/util-models';
import { OrderQuantityFormGroup, QuantityType, TradeFacade, TradeService, TradingTimesService } from '@zero/trade/domain';
import { distinctUntilChanged, filter } from 'rxjs';

@Directive()
export class TradeCreateQuantityBaseComponent implements OnInit, OnChanges {
  @Input() public asset?: Asset;
  @Input() public quote?: Quote;
  @Input() public execType?: ExecutionType;
  @Input() public form?: FormGroup<OrderQuantityFormGroup>;
  @Input() public direction?: OrderDirection = 'buy';
  @Input() public positionInSelectedAsset?: Position;
  @Input() public mode?: QuantityType;
  @Input() public value?: number;

  @Output() public changeMode = new EventEmitter<QuantityType>();
  @Output() public changeValue = new EventEmitter<number>();

  public shouldDisplayHighVolumeWarning = false;
  public hasFraction = false;
  public readonly InstrumentType = InstrumentType;
  public displayValue?: string;
  public displayValidity?: boolean;

  protected readonly tradingTimesService = inject(TradingTimesService);
  public readonly tradeService = inject(TradeService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #tradeFacade = inject(TradeFacade);

  public ngOnInit(): void {
    this.#tradeFacade.loadHolidays();
    this.form.valueChanges
      .pipe(
        distinctUntilChanged((previus, current) => previus.quantity === current.quantity),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(({ quantity }) => {
        this.shouldDisplayHighVolumeWarning = this.#showHighVolumeWarning(Number(quantity));
      });

    this.#tradeFacade.holidays$
      .pipe(
        filter((holidays) => holidays?.length > 0),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.#presetValidToDate());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.shouldDisplayHighVolumeWarning = this.#showHighVolumeWarning(Number(this.form.get('quantity')?.value));

    if (this.mode === 'value') {
      if (changes['quote']?.currentValue || changes['value']?.previousValue !== changes['value']?.currentValue) {
        this.#calcQuantity();
      }
    }

    if (!this.form.get('validToDate').value) {
      this.#presetValidToDate();
    }

    this.hasFraction = Number(this.positionInSelectedAsset?.availableQuantity) && this.positionInSelectedAsset.availableQuantity % 1 > 0;
    this.displayValidity = ['stop', 'limit', 'oco', 'tsl'].includes(this.execType);
  }

  public handleValueChange = (value: string): void => {
    this.#setValue(value);
    this.#calcQuantity();
  };

  public toggleMode = (mode: QuantityType): void => {
    this.mode = mode;
    if (this.mode === 'value') {
      this.calcValue();
    } else {
      this.#calcQuantity();
    }

    this.changeMode.emit(this.mode);
  };

  protected calcValue = (): void => {
    if (!this.form.get('quantity')?.value) {
      return;
    }
    const quantity = Number(this.form.get('quantity').value);
    if (!isNaN(quantity)) {
      this.#setValue((quantity * this.#getQuote2Calc()).toFixed(2));
    }
  };

  public handleWheel = (ev: Event): void => NumericInputHelperUtil.handleWheel(ev);

  #setValue = (value: string): void => {
    const valueWithDot = value.replace(',', '.');
    const parsedValue = value ? Number(valueWithDot) : null;
    if (isNaN(parsedValue)) {
      return;
    }

    this.displayValue = value.replace('.', ',');
    this.value = parsedValue;
    this.changeValue.emit(this.value);
  };

  #getQuote2Calc = (): number => {
    const { ask, bid } = this.quote;
    return this.direction === 'buy' ? ask : bid;
  };

  #calcQuantity = (): void => {
    let quantity: number | null = null;
    if (this.quote && this.value) {
      const { qtyScale, qtyIncr, instrumentType, fractionalTrading } = this.asset;
      const quote = this.#getQuote2Calc();
      const calculatedQuantity =
        qtyScale && qtyIncr ? +(this.value / quote - ((this.value / quote) % qtyIncr)).toFixed(qtyScale) : Math.floor(this.value / quote);
      switch (instrumentType) {
        case InstrumentType.Share:
        case InstrumentType.Etf:
        case InstrumentType.Funds:
          quantity =
            fractionalTrading || (this.direction === 'sell' && this.positionInSelectedAsset?.availableQuantity % 1 !== 0)
              ? +(this.value / quote).toFixed(3)
              : calculatedQuantity;
          break;
        default:
          quantity = calculatedQuantity;
          break;
      }
    }
    this.form.patchValue({ quantity });
  };

  #presetValidToDate(): void {
    const datePreset = this.tradingTimesService.getValidToDatePreset(this.tradingTimesService.getCurrentLocalDateTime());
    this.form.patchValue({
      validToDate: new Date(Date.UTC(datePreset.getFullYear(), datePreset.getMonth(), datePreset.getDate())),
    });
  }

  #showHighVolumeWarning(quantity?: number): boolean {
    if (!quantity || isNaN(quantity) || !this.quote) {
      return false;
    }

    const { askv, bidv } = this.quote;
    if (this.direction === 'buy' && askv) {
      return quantity > askv;
    } else if (this.direction === 'sell' && bidv) {
      return quantity > bidv;
    }

    return false;
  }

  public get quantity(): AbstractControl {
    return this.form?.get('quantity');
  }

  public setMinimumQuantity(quantity: number): void {
    this.form.patchValue({
      quantity,
    });
    this.calcValue();
  }
}
